import React, { useCallback, useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtDT } from "@/lib/platform";

const blank = { reason: "", ticket: "", hours: 4, allow_write: false, allow_restricted: false };

const StartModal = ({ open, onOpenChange, onStarted }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(blank);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/support-mode/start", f);
      toast.success(
        data.status === "PENDING_APPROVAL" ? "Support Mode request sent to the organization owner" : "Support Mode started"
      );
      onOpenChange(false);
      onStarted();
    } catch (e) {
      toast.error(apiError(e, "Could not start Support Mode"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="support-mode-start-dialog">
        <DialogHeader>
          <DialogTitle>Start Support Mode</DialogTitle>
          <DialogDescription>
            Time-limited, logged access to this organization's records for support purposes. The Organization
            Owner is notified when Support Mode starts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Reason (required)</Label>
            <Textarea rows={2} value={f.reason} onChange={(e) => setF((x) => ({ ...x, reason: e.target.value }))}
                      data-testid="support-mode-reason-input" />
          </div>
          <div className="space-y-1">
            <Label>Ticket (optional)</Label>
            <Input value={f.ticket} onChange={(e) => setF((x) => ({ ...x, ticket: e.target.value }))}
                   data-testid="support-mode-ticket-input" />
          </div>
          <div className="space-y-1">
            <Label>Duration (hours, max 24)</Label>
            <Input type="number" min={1} max={24} value={f.hours}
                   onChange={(e) => setF((x) => ({ ...x, hours: Number(e.target.value) }))}
                   data-testid="support-mode-hours-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={f.allow_restricted} onCheckedChange={(v) => setF((x) => ({ ...x, allow_restricted: !!v }))}
                      data-testid="support-mode-allow-restricted" />
            Include Restricted / legal-hold evidence
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={f.allow_write} onCheckedChange={(v) => setF((x) => ({ ...x, allow_write: !!v }))}
                      data-testid="support-mode-allow-write" />
            Allow write access (default is read-only)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || f.reason.trim().length < 5} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="support-mode-start-submit">
            {busy ? "Starting…" : "Start Support Mode"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Countdown = ({ expiresAt }) => {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      if (ms <= 0) {
        setLeft("expired");
        return;
      }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setLeft(`${h}h ${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return <span className="font-mono">{left}</span>;
};

export const SupportModeBar = () => {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const isPA = auth.user?.grc_role === "platform_admin";
  if (!isPA) return null;
  const s = auth.supportStatus;

  const end = async (id) => {
    try {
      await api.post(`/platform/support-mode/${id}/end`);
      toast.success("Support Mode ended");
      auth.refreshSupportStatus();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (s?.active) {
    return (
      <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-xs px-4 md:px-8 py-2 flex items-center gap-3"
           data-testid="support-mode-banner-active">
        <ShieldCheck className="w-4 h-4 shrink-0" />
        <span>
          Support Mode active in <b>{auth.user?.tenant_name}</b> · expires in <Countdown expiresAt={s.active.expires_at} />
        </span>
        <span className="text-emerald-700/70 truncate hidden md:inline">— {s.active.reason}</span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" className="h-6 px-2 text-emerald-900 hover:bg-emerald-100"
                onClick={() => end(s.active.id)} data-testid="support-mode-end-button">
          <X className="w-3 h-3 mr-1" /> End
        </Button>
      </div>
    );
  }
  if (s?.pending) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs px-4 md:px-8 py-2 flex items-center gap-3"
           data-testid="support-mode-banner-pending">
        <Clock className="w-4 h-4 shrink-0" /> Support Mode request awaiting owner approval.
      </div>
    );
  }
  return (
    <div className="bg-slate-100 border-b border-slate-200 text-slate-600 text-xs px-4 md:px-8 py-2 flex items-center gap-3 flex-wrap"
         data-testid="support-mode-banner-tier1">
      <ShieldAlert className="w-4 h-4 shrink-0" />
      <span>Platform Admin (Tier 1) — no access to record content, evidence or personal data in <b>{auth.user?.tenant_name}</b>.</span>
      <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setOpen(true)} data-testid="support-mode-start-button">
        Start Support Mode
      </Button>
      <StartModal open={open} onOpenChange={setOpen} onStarted={auth.refreshSupportStatus} />
    </div>
  );
};

export const SupportRequestsPanel = () => {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const isPA = auth.user?.grc_role === "platform_admin";
  const canDecide = auth.can("users", "edit") && !isPA;

  const load = useCallback(
    () => api.get("/platform/support-mode/requests").then(({ data }) => setItems(data.items)).catch(() => {}),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, approve) => {
    try {
      await api.post(`/platform/support-mode/${id}/decide`, { approve });
      toast.success(approve ? "Support Mode approved" : "Support Mode denied");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="support-requests-panel">
      <div className="text-xs text-slate-500">
        Platform Admin support-access sessions for this organization — every session is time-limited and logged.
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="support-requests-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-4 py-3">Admin</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Hours</th>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id} className="border-b border-slate-50" data-testid={`support-request-row-${g.id}`}>
                <td className="px-4 py-3 font-medium text-[#003366]">{g.grantee_username}</td>
                <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{g.reason}{g.ticket ? ` (${g.ticket})` : ""}</td>
                <td className="px-4 py-3">{g.hours}h</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {g.allow_write ? "Read/Write" : "Read-only"}{g.allow_restricted ? " · Restricted" : ""}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold" data-testid={`support-request-status-${g.id}`}>{g.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDT(g.created_at)}</td>
                <td className="px-4 py-3">
                  {g.status === "PENDING_APPROVAL" && canDecide && (
                    <div className="flex gap-1">
                      <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => decide(g.id, true)} data-testid={`support-request-approve-${g.id}`}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2"
                              onClick={() => decide(g.id, false)} data-testid={`support-request-deny-${g.id}`}>
                        Deny
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-500" data-testid="support-requests-empty">
                  No Support Mode sessions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
